import React from 'react';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {Box, Stack, Drawer, Typography, ListItemButton} from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';
import { Icon } from '@iconify/react';

import { usePathname } from '../../routes/hooks';
import { RouterLink } from '../../routes/components';
import { useResponsive } from '../../hooks/use-responsive';
import Scrollbar from '../../components/scrollbar';
import { NAV } from './config-layout';
import useNavConfig, {
  eventsIcon,
  integrationsIcon,
} from './config-navigation';
import { Link } from 'react-router-dom';
import { useAvailableActions } from '../../hooks/useAvailableActions';
import { useTranslations } from '../../hooks/use-translations';
import { sidebarFont } from '../../theme/typography';

const NAV_BG = '#374852';
const NAV_LINK_COLOR = '#fff';
const navBackgroundSx = {
  backgroundColor: NAV_BG,
  backgroundImage: `linear-gradient(165deg, ${lighten(NAV_BG, 0.15)} 0%, ${NAV_BG} 55%)`,
};
const navPaperSx = {
  ...navBackgroundSx,
  fontFamily: sidebarFont,
  '& a': {
    color: NAV_LINK_COLOR,
  },
};

const navItemSx = (active) => ({
  minHeight: 44,
  borderRadius: 0.75,
  typography: 'body2',
  color: NAV_LINK_COLOR,
  textTransform: 'capitalize',
  fontWeight: 'fontWeightMedium',
  ...(active && {
    color: NAV_LINK_COLOR,
    fontWeight: 'fontWeightSemiBold',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
    '&:hover': {
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
    },
  }),
});

// ----------------------------------------------------------------------

export default (function Nav({ openNav, onCloseNav }) {
  const pathname = usePathname();
  const upLg = useResponsive('up', 'lg');
  const { account } = useSelector((state) => state.app);
  const siteId = useSelector((state) => state.app.siteId);
  const { baseNavConfig, eventsNavConfig, base2NavConfig, integrationsNavConfig } = useNavConfig();
  const { t } = useTranslations();

  const {
    canReadEvents,
    canReadProfiles,
    canReadBoscaAdminReports,
  } = useAvailableActions();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderCompanyLogo = (
    <Box
      component={Link}
      to="/"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: 1,
        flexShrink: 0,
        gap: 1,
        px: 2,
        pt: 2.5,
        pb: 1.5,
        textDecoration: 'none',
      }}
    >
      <NavLogo src={account?.logoPath} alt="Company Logo" />
      <NavLogo src={account?.resellerLogoPath} alt="Reseller Logo" compact />
    </Box>
  );

  // const renderAccount = (
  //   <Box
  //     sx={{
  //       my: 3,
  //       mx: 2.5,
  //       py: 2,
  //       px: 2.5,
  //       display: 'flex',
  //       borderRadius: 1.5,
  //       alignItems: 'center',
  //       bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
  //     }}
  //   >
  //     <Avatar src="/assets/images/avatars/user.png" alt="photoURL" />
  //     <Box sx={{ ml: 2 }}>
  //       <Typography variant="subtitle2">{auth.user?.profile.name}</Typography>
  //     </Box>
  //   </Box>
  // );

  let eventsActive = false;
  let integrationsActive = false;

  let [showEvents, setShowEvents] = React.useState(false);
  let [showIntegrations, setShowIntegrations] = React.useState(false);

  function toggleEvents() {
    setShowEvents((showEvents) => !showEvents);
  }
  function toggleIntegrations() {
    setShowIntegrations((showIntegrations) => !showIntegrations);
  }
  for (let i = 0; i < eventsNavConfig.length; i++) {
    if (eventsNavConfig[i].path === pathname) {
      eventsActive = true;
    }
  }
  for (let i = 0; i < integrationsNavConfig.length; i++) {
    if (integrationsNavConfig[i].path === pathname) {
      integrationsActive = true;
    }
  }

  const renderMenu = (
    <Stack component="nav" spacing={0.5} sx={{ px: 2, flexShrink: 0 }}>
      {baseNavConfig.map((item) => (<NavItem key={item.title} item={item} /> ))}

      {canReadEvents && (
        <ListItemButton
          component={RouterLink}
          onClick={toggleEvents}
          sx={navItemSx(eventsActive)}
        >
          <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
            {eventsIcon}
          </Box>
          <Box component="span">{t('navigation.events')}</Box>
          <Box component="span" sx={{ width: 24, height: 24, mr: 2, marginRight: '0px', marginLeft: 'auto' }}>
            {showEvents ? (
              <Icon icon="fe:arrow-up" width="24" height="24" />
            ) : (
              <Icon icon="fe:arrow-right" width="24" height="24" />
            )}
          </Box>
        </ListItemButton>
      )}

      {canReadEvents &&
        showEvents &&
        eventsNavConfig.map((item) => <ReportsNavItem key={item.title} item={item} />)}

      {canReadBoscaAdminReports && integrationsNavConfig.length > 0 && (
        <ListItemButton
          component={RouterLink}
          onClick={toggleIntegrations}
          sx={navItemSx(integrationsActive)}
        >
          <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
            {integrationsIcon}
          </Box>
          <Box component="span">{t('navigation.integrations')}</Box>
          <Box component="span" sx={{ width: 24, height: 24, mr: 2, marginRight: '0px', marginLeft: 'auto' }}>
            {showIntegrations ? (
              <Icon icon="fe:arrow-up" width="24" height="24" />
            ) : (
              <Icon icon="fe:arrow-right" width="24" height="24" />
            )}
          </Box>
        </ListItemButton>
      )}

      {canReadBoscaAdminReports &&
        showIntegrations &&
        integrationsNavConfig.map((item) => <ReportsNavItem key={item.title} item={item} />)}

      {base2NavConfig.map((item) => (<NavItem key={item.title} item={item} /> ))}

      {siteId && canReadProfiles && (
        <NavItem key={t('navigation.manageProfiles')} item={{
          title: t('navigation.profiles'),
          path: `/profiles/${siteId}`,
          icon: <Icon icon="material-symbols:account-circle" width="24" height="24" />,
        }} />
      )}
    </Stack>
  );

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
        '& a': {
          color: NAV_LINK_COLOR,
        },
        '& .simplebar-mask': {
          ...navBackgroundSx,
        },
      }}
    >
      {renderCompanyLogo}
      {renderMenu}
      <Box sx={{ flexGrow: 1 }} />
      <Typography color="#c3c3c3" pl="2rem" fontSize={14}>{t('navigation.poweredBy')}</Typography>
      <Box
        component="img"
        src="/assets/bosca_logo.png"
        alt="Cloud Loq Logo"
        style={{ width: '12rem', height: '3rem', marginLeft: 'auto', marginRight: 'auto', marginBottom: '3rem' }}
      />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.WIDTH },
      }}
    >
      {upLg ? (
        <Box
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.WIDTH,
            ...navPaperSx,
          }}
        >
          {renderContent}
        </Box>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          keepMounted
          PaperProps={{
            sx: {
              width: NAV.WIDTH,
              ...navPaperSx,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
});

// ----------------------------------------------------------------------

function NavLogo({ src, alt, compact = false }) {
  if (!src) {
    return null;
  }

  const plateHeight = compact ? 52 : 76;
  const imageHeight = compact ? 36 : 52;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 1,
        height: plateHeight,
        flexShrink: 0,
        px: 1.5,
        bgcolor: '#fff',
        borderRadius: 1.5,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.22)',
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          display: 'block',
          width: '100%',
          height: imageHeight,
          objectFit: 'contain',
        }}
      />
    </Box>
  );
}

function NavItem({ item }) {
  const pathname = usePathname();
  const active = item.path === pathname;

  return (
    <ListItemButton
      component={RouterLink}
      href={item.path}
      sx={navItemSx(active)}
    >
      <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
        {item.icon}
      </Box>

      <Box component="span">{item.title} </Box>
    </ListItemButton>
  );
}

function ReportsNavItem({ item }) {
  const pathname = usePathname();
  const active = item.path === pathname;

  return (
    <ListItemButton
      component={RouterLink}
      href={item.path}
      sx={{
        minHeight: 30,
        borderRadius: 0,
        typography: 'body2',
        color: NAV_LINK_COLOR,
        textTransform: 'capitalize',
        fontWeight: 'fontWeightMedium',
        ...(active && {
          color: 'light grey',
          fontWeight: 'fontWeightSemiBold',
          bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
          },
        }),
      }}
    >
      <Box component="span" sx={{ width: 24, height: 24, ml: 5 }}>
        {item.icon}
      </Box>

      <Box style={{ marginLeft: 15 }}>{item.title} </Box>
    </ListItemButton>
  );
}

NavLogo.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string.isRequired,
  compact: PropTypes.bool,
};

NavItem.propTypes = {
  item: PropTypes.object,
};
